import { useCallback, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ValidatedCoach {
  id: string;
  user_id: string;
  display_name: string;
  discount_code: string;
  discount_pct: number;
  commission_pct: number;
}

/**
 * Coach discount code state for checkout.
 *
 * Validation is a single SELECT against the public-readable `coaches` table
 * (RLS policy "Active coaches publicly readable" allows the lookup without
 * auth). The shopper enters a coach's code, we resolve it to a row, and the
 * checkout flow:
 *   1) subtracts `coachDisc` from the order total (separate from PROMO_CODES)
 *   2) records a `coach_attributions` row after the order is created, so the
 *      trainer's portal shows the sale and accrues commission.
 */
export function useCoachCode(cartTot: number) {
  const [coachCodeInput, setCoachCodeInput] = useState("");
  const [validatedCoach, setValidatedCoach] = useState<ValidatedCoach | null>(null);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyCoachCode = useCallback(
    async (lang: "es" | "en" = "es") => {
      const code = coachCodeInput.trim();
      if (!code) return { ok: false, message: lang === "es" ? "Escribe un código" : "Enter a code" };
      setValidating(true);
      setError(null);
      const { data, error: err } = await supabase
        .from("coaches")
        .select("id,user_id,display_name,discount_code,discount_pct,is_active")
        .ilike("discount_code", code)
        .eq("is_active", true)
        .maybeSingle();
      setValidating(false);
      if (err || !data) {
        setError(lang === "es" ? "Código no válido" : "Invalid code");
        return { ok: false, message: lang === "es" ? "Código no válido" : "Invalid code" };
      }
      const coach: ValidatedCoach = {
        id: String(data.id),
        user_id: String(data.user_id),
        display_name: String(data.display_name),
        discount_code: String(data.discount_code),
        discount_pct: Number(data.discount_pct) || 0,
        commission_pct: 0,
      };
      setValidatedCoach(coach);
      setCoachCodeInput("");
      return {
        ok: true,
        coach,
        message:
          lang === "es"
            ? `Código de ${coach.display_name} aplicado (-${coach.discount_pct}%)`
            : `${coach.display_name}'s code applied (-${coach.discount_pct}%)`,
      };
    },
    [coachCodeInput],
  );

  const clearCoach = useCallback(() => {
    setValidatedCoach(null);
    setError(null);
  }, []);

  // Discount in same currency unit as cartTot (USD in this app).
  const coachDisc = useMemo(() => {
    if (!validatedCoach) return 0;
    return Math.max(0, cartTot * (validatedCoach.discount_pct / 100));
  }, [validatedCoach, cartTot]);

  return {
    coachCodeInput,
    setCoachCodeInput,
    validatedCoach,
    applyCoachCode,
    clearCoach,
    coachDisc,
    validating,
    coachError: error,
  };
}